import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './AddEventPage.css'; // reuse the same form styles

const INITIAL = {
  min_required_android: '1.0.0',
  latest_android:       '1.0.0',
  android_store_url:    'https://play.google.com/store/apps/details?id=com.example.team_fronx',
  min_required_ios:     '1.0.0',
  latest_ios:           '1.0.0',
  ios_store_url:        'https://apps.apple.com/app/id0000000000',
  release_notes:        'Bug fixes\nDesign and User Experience enhancements',
};

export default function AppVersionPage() {
  const [form,       setForm]       = useState(INITIAL);
  const [loaded,     setLoaded]     = useState(false);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'app_config', 'version'));
        if (snap.exists()) setForm({ ...INITIAL, ...snap.data() });
      } catch { /* fall through with defaults */ }
      setLoaded(true);
    })();
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    setErrors(p => ({ ...p, [name]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    const semver = /^\d+\.\d+\.\d+$/;
    if (!semver.test(form.min_required_android.trim()))
      e.min_required_android = 'Use semver, e.g. 1.0.5';
    if (!semver.test(form.latest_android.trim()))
      e.latest_android = 'Use semver, e.g. 1.0.7';
    if (!form.android_store_url.trim())
      e.android_store_url = 'Play Store URL is required.';
    if (!semver.test(form.min_required_ios.trim()))
      e.min_required_ios = 'Use semver, e.g. 1.0.5';
    if (!semver.test(form.latest_ios.trim()))
      e.latest_ios = 'Use semver, e.g. 1.0.7';
    if (!form.ios_store_url.trim())
      e.ios_store_url = 'App Store URL is required.';
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    const ok = window.confirm(
      `Set min required version to ${form.min_required_android.trim()}?\n\n` +
      `Members on older versions will be FORCED to update before they ` +
      `can use the app.`
    );
    if (!ok) return;

    setSubmitting(true);
    try {
      await setDoc(doc(db, 'app_config', 'version'), {
        min_required_android: form.min_required_android.trim(),
        latest_android:       form.latest_android.trim(),
        android_store_url:    form.android_store_url.trim(),
        min_required_ios:     form.min_required_ios.trim(),
        latest_ios:           form.latest_ios.trim(),
        ios_store_url:        form.ios_store_url.trim(),
        release_notes:        form.release_notes.trim(),
        updated_at:           serverTimestamp(),
      });
      setToast({ type: 'success', message: '✅ Version config saved. Members on older versions will see the update screen on next app open.' });
    } catch (err) {
      setToast({ type: 'error', message: `❌ Failed: ${err.message}` });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  if (!loaded) {
    return (
      <div className="mem-loading">
        <span className="tf-spinner tf-spinner--red" />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">App Version</h1>
          <p className="page-subtitle">
            Force users to update by raising the minimum required version.
            Affects the Android app immediately on next launch.
          </p>
        </div>
      </div>

      {toast && (
        <div
          className={`amf-toast amf-toast--${toast.type}`}
          style={{ marginBottom: 24 }}
        >
          {toast.message}
          <button className="amf-toast-close" onClick={() => setToast(null)}>×</button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div className="aep-grid">

          <div className="aep-col">
            <PlatformGroup label="🤖 Android">
              <Field label="Minimum Required Version" required
                     error={errors.min_required_android}>
                <input name="min_required_android"
                  className={`amf-input ${errors.min_required_android ? 'amf-input--error' : ''}`}
                  placeholder="1.0.5"
                  value={form.min_required_android} onChange={handleChange} />
                <p className="amf-hint">
                  Users below this version get the blocking
                  <strong> Please Update Your App </strong>
                  screen.
                </p>
              </Field>
              <Field label="Latest Version" required
                     error={errors.latest_android}>
                <input name="latest_android"
                  className={`amf-input ${errors.latest_android ? 'amf-input--error' : ''}`}
                  placeholder="1.0.7"
                  value={form.latest_android} onChange={handleChange} />
              </Field>
              <Field label="Play Store URL" required
                     error={errors.android_store_url}>
                <input name="android_store_url" type="url"
                  className={`amf-input ${errors.android_store_url ? 'amf-input--error' : ''}`}
                  placeholder="https://play.google.com/store/apps/details?id=…"
                  value={form.android_store_url} onChange={handleChange} />
              </Field>
            </PlatformGroup>

            <PlatformGroup label="🍎 iOS">
              <Field label="Minimum Required Version" required
                     error={errors.min_required_ios}>
                <input name="min_required_ios"
                  className={`amf-input ${errors.min_required_ios ? 'amf-input--error' : ''}`}
                  placeholder="1.0.5"
                  value={form.min_required_ios} onChange={handleChange} />
              </Field>
              <Field label="Latest Version" required
                     error={errors.latest_ios}>
                <input name="latest_ios"
                  className={`amf-input ${errors.latest_ios ? 'amf-input--error' : ''}`}
                  placeholder="1.0.7"
                  value={form.latest_ios} onChange={handleChange} />
              </Field>
              <Field label="App Store URL" required
                     error={errors.ios_store_url}>
                <input name="ios_store_url" type="url"
                  className={`amf-input ${errors.ios_store_url ? 'amf-input--error' : ''}`}
                  placeholder="https://apps.apple.com/app/idXXXXXXXXX"
                  value={form.ios_store_url} onChange={handleChange} />
                <p className="amf-hint">
                  Replace the placeholder ID once you publish to App Store.
                </p>
              </Field>
            </PlatformGroup>

            <Field label="Release Notes (shared, both platforms)">
              <textarea name="release_notes"
                className="amf-input aep-textarea"
                rows={5}
                placeholder={'Bug fixes\nDesign and UX enhancements'}
                value={form.release_notes} onChange={handleChange} />
              <p className="amf-hint">
                One bullet per line. Shown on the update screen as a list.
              </p>
            </Field>
          </div>

          <div className="aep-col">
            <Field label="Live Preview">
              <div style={{
                background: 'linear-gradient(180deg, #0F0F0F, #09090B)',
                border: '1.5px solid rgba(212, 181, 116, 0.25)',
                borderRadius: 14,
                padding: 22,
                color: '#F7E8BF',
              }}>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <img src="/logo.png" alt="" style={{ width: 60, height: 60 }} />
                </div>
                <div style={{
                  fontSize: 20,
                  fontWeight: 900,
                  marginBottom: 10,
                  letterSpacing: -0.3,
                }}>
                  Please Update Your APP
                </div>
                <div style={{
                  color: '#D8C8A4',
                  fontSize: 13,
                  lineHeight: 1.55,
                  marginBottom: 10,
                }}>
                  We have a new updated version with more enhancements!
                  For a better experience, we recommend updating your
                  app in order to continue using it.
                </div>
                <div style={{
                  color: '#D8C8A4',
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 4,
                }}>
                  Our update includes:
                </div>
                {form.release_notes.split('\n').map((b, i) => {
                  const t = b.trim();
                  if (!t) return null;
                  return (
                    <div key={i} style={{
                      display: 'flex',
                      gap: 8,
                      marginBottom: 3,
                      color: '#D8C8A4',
                      fontSize: 13,
                    }}>
                      <span style={{ color: '#D4B574' }}>•</span>
                      <span>{t}</span>
                    </div>
                  );
                })}
                <div style={{
                  marginTop: 18,
                  textAlign: 'center',
                  padding: '10px',
                  background: '#D4B574',
                  color: '#000',
                  borderRadius: 8,
                  fontWeight: 900,
                  letterSpacing: 1.5,
                  fontSize: 14,
                }}>
                  UPDATE APP
                </div>
                <div style={{
                  marginTop: 10,
                  textAlign: 'center',
                  color: '#B79F6D',
                  fontSize: 10,
                  letterSpacing: 0.5,
                }}>
                  Required {form.min_required_android}
                </div>
              </div>
            </Field>
          </div>
        </div>

        <div className="aep-submit-row">
          <button
            type="submit"
            className="amf-submit-btn"
            disabled={submitting}
          >
            {submitting
              ? <><span className="amf-spinner" /> Saving…</>
              : 'Save Version Config'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}

function PlatformGroup({ label, children }) {
  return (
    <div style={{
      marginBottom: 20,
      padding: 16,
      background: 'rgba(212, 181, 116, 0.04)',
      border: '1px solid rgba(212, 181, 116, 0.18)',
      borderRadius: 10,
    }}>
      <div style={{
        color: '#D4B574',
        fontSize: 11,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        fontWeight: 700,
        marginBottom: 12,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}
